/**
 * useAgentsLive — Sep 2026 · r-agent-feed
 *
 * Small singleton-style hook that polls `/agents/live` every 60s and
 * shares the payload across every consumer (sidebar chip + floating
 * console widget). Uses a module-level cache + subscriber set so N
 * mounted components result in exactly 1 network call.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

const _cache = {
  wsId: null,
  data: null,       // { live_count, total, agents: [...] }
  fetchedAt: 0,
  inflight: null,
};
const _subs = new Set();

async function _fetchNow(wsId) {
  if (!wsId) return null;
  if (_cache.inflight && _cache.wsId === wsId) return _cache.inflight;
  const p = (async () => {
    try {
      const { data } = await api.get(`/workspaces/${wsId}/agents/live`);
      _cache.wsId = wsId;
      _cache.data = data;
      _cache.fetchedAt = Date.now();
      _subs.forEach((fn) => { try { fn(data); } catch { /* ignore */ } });
      return data;
    } catch {
      return null;
    } finally {
      _cache.inflight = null;
    }
  })();
  _cache.inflight = p;
  return p;
}

export function useAgentsLive() {
  const { activeWorkspaceId } = useAuth();
  const wsId = activeWorkspaceId;
  const [data, setData] = useState(_cache.wsId === wsId ? _cache.data : null);

  useEffect(() => {
    if (!wsId) return undefined;
    const cb = (d) => setData(d);
    _subs.add(cb);
    // Fire immediately if the cache is stale (> 90s) or workspace changed.
    const stale = _cache.wsId !== wsId || (Date.now() - _cache.fetchedAt) > 90000;
    if (stale) _fetchNow(wsId);
    else if (_cache.data) setData(_cache.data);
    // Poll every 60s while at least one subscriber is mounted.
    // Feb 2026 R281 · Visibility-gated so background tabs stop hitting.
    const stopPoll = pollWhenVisible(() => _fetchNow(wsId), 60000);
    return () => {
      _subs.delete(cb);
      stopPoll();
    };
  }, [wsId]);

  return data;
}

/** Force a re-fetch on demand (e.g. after a compliance-monitor sweep). */
export function refreshAgentsLive() {
  if (_cache.wsId) _fetchNow(_cache.wsId);
}
